/**
 * NetworkCheck - Network/connection verification step
 * Nordic Sanctuary design: Clear feedback, quality indicators
 *
 * Bandwidth thresholds based on LiveKit/OpenVidu WebRTC requirements:
 * - 720p H.264 video requires ~1.25 Mbps per stream
 * - 1080p H.264 video requires ~2.70 Mbps per stream
 * - Bidirectional calls need both upload and download bandwidth
 *
 * Thresholds (with headroom for stability):
 * - Excellent: ≥10 Mbps (supports 1080p with overhead)
 * - Good: ≥10 Mbps
 * - Acceptable: ≥3 Mbps (supports 720p bidirectional)
 * - Poor: <3 Mbps
 *
 * @see https://livekit.io/webrtc/bitrate-guide
 */

import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Wifi, WifiOff, RefreshCw, CheckCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@jourfx/ui";

interface NetworkCheckProps {
  onResult: (passed: boolean) => void;
  onContinue: () => void;
}

type Quality = "excellent" | "good" | "fair" | "poor";

// Bandwidth thresholds in Mbps
const THRESHOLDS = {
  EXCELLENT: 10, // Supports 1080p with excellent headroom
  GOOD: 10, // Supports 1080p comfortably
  ACCEPTABLE: 3, // Minimum for stable 720p bidirectional call
} as const;

// Navigator connection API type
interface NetworkInformation {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  downlink?: number; // Mbps
  rtt?: number; // ms
}

/**
 * Measure network quality by testing latency to the app's own origin.
 * This tests the actual infrastructure the video call will use,
 * not an unrelated CDN.
 *
 * For bandwidth, we prefer navigator.connection.downlink (reports the
 * effective link speed) because small-file download timing is dominated
 * by connection overhead and gives wildly inaccurate results.
 */
async function measureBandwidth(): Promise<number> {
  // 1. Navigator Connection API — best signal for actual link speed
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (connection?.downlink && connection.downlink > 0) {
    return connection.downlink;
  }

  // 2. Latency-based estimate against our own origin
  //    Multiple round-trips to warm the connection, then measure
  const origin = window.location.origin;
  const latencies: number[] = [];

  for (let i = 0; i < 3; i++) {
    try {
      const start = performance.now();
      await fetch(origin + "/favicon.ico?t=" + Date.now(), {
        cache: "no-store",
        mode: "same-origin",
      });
      const latency = performance.now() - start;
      if (latency > 0 && isFinite(latency)) {
        latencies.push(latency);
      }
    } catch {
      // Origin may not serve favicon — try root
      try {
        const start = performance.now();
        await fetch(origin + "/?_healthcheck=" + Date.now(), {
          cache: "no-store",
          method: "HEAD",
        });
        const latency = performance.now() - start;
        if (latency > 0 && isFinite(latency)) {
          latencies.push(latency);
        }
      } catch {
        // Offline or blocked
      }
    }
  }

  if (latencies.length === 0) {
    return 0; // Cannot reach own server
  }

  // Use the best (lowest) latency — first request has cold-start overhead
  const bestLatency = Math.min(...latencies);

  // Map latency to estimated bandwidth
  // Local/same-network: <10ms → excellent
  // Good connection: <50ms → good
  // Acceptable: <150ms → fair
  // Poor: ≥150ms
  if (bestLatency < 10) return 100;
  if (bestLatency < 30) return 50;
  if (bestLatency < 50) return 25;
  if (bestLatency < 100) return 15;
  if (bestLatency < 150) return 8;
  if (bestLatency < 300) return 4;
  return 2;
}

/**
 * Determine quality level from measured speed
 */
function getQualityFromSpeed(speedMbps: number): Quality {
  if (speedMbps >= THRESHOLDS.EXCELLENT) return "excellent";
  if (speedMbps >= THRESHOLDS.GOOD) return "good";
  if (speedMbps >= THRESHOLDS.ACCEPTABLE) return "fair";
  return "poor";
}

export function NetworkCheck({ onResult, onContinue }: NetworkCheckProps) {
  const { t } = useTranslation("techcheck");
  const [status, setStatus] = useState<"checking" | "done">("checking");
  const [quality, setQuality] = useState<Quality | null>(null);
  const [speed, setSpeed] = useState<number | null>(null);

  const checkNetwork = useCallback(async () => {
    setStatus("checking");
    setQuality(null);
    setSpeed(null);

    try {
      // First, quick check using Navigator Connection API (if available)
      const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;

      // If connection API indicates very poor connection, fail fast
      if (connection?.effectiveType === "slow-2g" || connection?.effectiveType === "2g") {
        setQuality("poor");
        setSpeed(connection.downlink ?? 1);
        setStatus("done");
        onResult(false);
        return;
      }

      // Measure actual bandwidth
      const measuredSpeed = await measureBandwidth();
      const measuredQuality = getQualityFromSpeed(measuredSpeed);

      setSpeed(Math.round(measuredSpeed * 10) / 10); // Round to 1 decimal
      setQuality(measuredQuality);
      setStatus("done");
      onResult(measuredQuality !== "poor");
    } catch (err) {
      console.error("Network check failed:", err);
      setQuality("poor");
      setSpeed(0);
      setStatus("done");
      onResult(false);
    }
  }, [onResult]);

  useEffect(() => {
    checkNetwork();
  }, []);

  const qualityConfig: Record<
    Quality,
    { label: string; iconClass: string; bgClass: string; shadowClass: string }
  > = {
    excellent: {
      label: t("checks.network.quality.excellent"),
      iconClass: "text-white",
      bgClass: "bg-[var(--sage-500)]",
      shadowClass: "shadow-lg shadow-[var(--sage-300)]/30",
    },
    good: {
      label: t("checks.network.quality.good"),
      iconClass: "text-white",
      bgClass: "bg-[var(--sage-500)]",
      shadowClass: "shadow-lg shadow-[var(--sage-300)]/30",
    },
    fair: {
      label: t("checks.network.quality.fair"),
      iconClass: "text-[oklch(0.55_0.15_80)]",
      bgClass: "bg-[oklch(0.92_0.08_80)]",
      shadowClass: "",
    },
    poor: {
      label: t("checks.network.quality.poor"),
      iconClass: "text-[oklch(0.55_0.18_25)]",
      bgClass: "bg-[oklch(0.95_0.04_25)]",
      shadowClass: "",
    },
  };

  const isPassed = quality === "excellent" || quality === "good";
  const isWarning = quality === "fair";
  const isFailed = quality === "poor";

  return (
    <div className="space-y-6">
      {/* Network indicator */}
      <div className="flex flex-col items-center py-6">
        <div
          className={`
            w-24 h-24 rounded-3xl flex items-center justify-center transition-all duration-300
            ${
              status === "checking"
                ? "bg-[var(--sage-100)]"
                : quality
                  ? `${qualityConfig[quality].bgClass} ${qualityConfig[quality].shadowClass}`
                  : "bg-[var(--stone-100)]"
            }
          `}
        >
          {status === "checking" ? (
            <Wifi
              className="w-10 h-10 text-[var(--sage-600)] animate-pulse"
              aria-hidden="true"
            />
          ) : isFailed ? (
            <WifiOff
              className={`w-10 h-10 ${quality ? qualityConfig[quality].iconClass : ""}`}
              aria-hidden="true"
            />
          ) : (
            <Wifi
              className={`w-10 h-10 ${quality ? qualityConfig[quality].iconClass : ""}`}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Checking state */}
        {status === "checking" && (
          <p className="mt-4 text-[var(--stone-500)]">{t("checks.network.checking")}</p>
        )}

        {/* Done state */}
        {status === "done" && quality && (
          <div className="mt-4 text-center">
            {/* Quality indicator */}
            <div
              className={`flex items-center justify-center gap-2 ${
                isPassed
                  ? "text-[var(--sage-600)]"
                  : isWarning
                    ? "text-[oklch(0.55_0.15_80)]"
                    : "text-[oklch(0.55_0.18_25)]"
              }`}
            >
              {isFailed ? (
                <AlertTriangle className="w-5 h-5" aria-hidden="true" />
              ) : isWarning ? (
                <AlertTriangle className="w-5 h-5" aria-hidden="true" />
              ) : (
                <CheckCircle className="w-5 h-5" aria-hidden="true" />
              )}
              <span className="font-semibold">{qualityConfig[quality].label}</span>
            </div>

            {/* Speed estimate */}
            {speed !== null && speed > 0 && (
              <p className="text-sm text-[var(--stone-500)] mt-1">
                {t("checks.network.measuredSpeed", { speed })}
              </p>
            )}

            {/* Bandwidth requirement hint */}
            {isPassed && (
              <p className="text-sm text-[var(--stone-400)] mt-2">
                {t("checks.network.recommendedSpeed")}
              </p>
            )}

            {/* Warning/Error messages */}
            {isFailed && (
              <p className="text-sm text-[oklch(0.50_0.15_25)] mt-3 max-w-sm">
                {t("checks.network.poorWarning")}
              </p>
            )}

            {isWarning && (
              <p className="text-sm text-[oklch(0.50_0.12_80)] mt-3 max-w-sm">
                {t("checks.network.fairWarning")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        {status === "done" && isFailed && (
          <Button variant="secondary" size="client" className="flex-1" onClick={checkNetwork}>
            <RefreshCw aria-hidden="true" />
            {t("actions.recheck")}
          </Button>
        )}
        <Button
          variant={isPassed ? "default" : "secondary"}
          size="client"
          className="flex-1"
          onClick={onContinue}
          disabled={status === "checking"}
        >
          {isPassed ? (
            <>
              {t("actions.continue")}
              <ArrowRight aria-hidden="true" />
            </>
          ) : (
            t("actions.skip")
          )}
        </Button>
      </div>

      {status === "done" && (isFailed || isWarning) && (
        <div className="rounded-xl border border-[var(--stone-200)] bg-[var(--stone-50)] p-4">
          <p className="text-xs text-[var(--stone-600)]">{t("troubleshooting.network")}</p>
        </div>
      )}
    </div>
  );
}
